import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "../../imports/muiImport";
import { useLocation, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import InfiniteScroll from "react-infinite-scroll-component";
import ProductCardsList from "./LARGEDEVICE/ProductCardsList";
import { getAllProducts, useSelector } from "../../imports/generalImport";
import ProductFilterMobile from "./MEDIUMDEVICE/mobileProductFilter";

const rearrangedProducts = {
  categories: [
    {
      name: 'Entertainment',
      id: 'e55c20bb-7e79-42a8-8bd7-d46fc97bd793',
      data: [
        "c95f9b99-4918-45fc-b9ac-f2e824db563a",
        "56b56a7b-b432-474d-a22d-d024b573a176",
        "6560c948-c657-4b9e-9a31-9d76795a1be1",
        "fb6bd19d-e195-4f03-8f10-9d0e064a53a0",
        "cfd1a870-05dd-4c32-978c-89d2825bcd42",
        "660a474b-457f-48ac-b5ab-bd6baa1b4eac",
        "a70d5a04-2ec4-43cd-95a5-46da8f45ce12",
        "aeb2717f-b350-4864-b002-de0261f85646",
        "ccb69007-3931-4506-862b-5fb2c6c1cf9c",
        "d932d356-07df-42ba-a34e-84d8d6361880",
        "f945816e-f525-4219-8bf4-5808b55eea6f"
      ]
    }
  ]
}

const rearrangedProductHandler = (idxs, products) => {
  const isNeedRearrangedProducts = idxs?.includes(
    rearrangedProducts?.categories?.[0]?.id
  );

  if (!isNeedRearrangedProducts) return products;

  const order = rearrangedProducts?.categories?.[0]?.data || [];

  const activeProducts = products
    .filter((item) => item?.node?.isActive)
    .sort((a, b) => {
      const aIndex = order.indexOf(a.node.id);
      const bIndex = order.indexOf(b.node.id);

      return (aIndex === -1 ? Infinity : aIndex) -
        (bIndex === -1 ? Infinity : bIndex);
    });

  let activeIndex = 0;

  return products.map((item) =>
    item?.node?.isActive
      ? activeProducts[activeIndex++]
      : item
  );
};

const MobileAllGiftCardPage = () => {
  const categories = useSelector(
    (state) => state?.Dashboard?.categories
  );
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [endCursor, setEndCursor] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const isFetchingMoreRef = useRef(false);

  const searchItem = useLocation();
  const canonicalUrl = `https://www.99gift.ae/${searchItem.pathname}`;

  const categoryIdsParam = searchParams.get("categoryIds[]");
  const categoryIds = useMemo(
    () => (categoryIdsParam ? categoryIdsParam.split(",") : []),
    [categoryIdsParam]
  );

  // Initial Fetch Products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);

        const payload = {
          limit: 8,
        };

        if (categoryIds.length > 0) {
          payload.categoryIds = categoryIds;
        }

        const response = await getAllProducts(payload);

        if (response?.success) {
          const shuffledProducts = rearrangedProductHandler(
            categoryIds,
            response?.data?.edges || []
          );
          setProducts(shuffledProducts || []);
          setHasMore(Boolean(response?.data?.pageInfo?.hasNextPage));
          setEndCursor(response?.data?.pageInfo?.endCursor || null);
        } else {
          setProducts([]);
          setHasMore(false);
          setEndCursor(null);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        setProducts([]);
        setHasMore(false);
        setEndCursor(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [searchParams]);

  // Fetch More Products for Infinite Scroll
  const fetchMoreProducts = async () => {
    if (!hasMore || !endCursor || isFetchingMoreRef.current) return;

    try {
      isFetchingMoreRef.current = true;
      const payload = {
        limit: 8,
        after: endCursor,
      };

      if (categoryIds.length > 0) {
        payload.categoryIds = categoryIds;
      }

      const response = await getAllProducts(payload);

      if (response?.success) {
        const newEdges = response?.data?.edges || [];
        setProducts((prev) => [...prev, ...newEdges]);
        setHasMore(Boolean(response?.data?.pageInfo?.hasNextPage));
        setEndCursor(response?.data?.pageInfo?.endCursor || null);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching more products:", error);
      setHasMore(false);
    } finally {
      isFetchingMoreRef.current = false;
    }
  };

  const pageTitle =
    categoryIds.length > 0
      ? categories
        ?.filter((cat) =>
          categoryIds.includes(String(cat?.id))
        )
        .map((cat) => cat.name)
        .join(", ")
      : "All Gift Cards";

  return (
    <>
      <Helmet>
        <title>Explore the Best Gift Cards</title>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      {/* Products */}
      <Container
        maxWidth="xl"
        sx={{
          px: {
            xs: 0,
            sm: 2,
            md: 0,
          },
        }}
      >
        {/* MOBILE CATEGORY SLIDER */}
        <Box
          sx={{
            display: {
              xs: "block",
              sm: "block",
              md: "none",
            },
            overflowX: "auto",
            whiteSpace: "nowrap",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <ProductFilterMobile
            categoryIds={categoryIds}
            setSearchParams={setSearchParams}
          />
        </Box>

        <Grid container spacing={3} id="target-section">
          {/* Product List */}
          <Grid
            item
            xs
            sx={{
              borderLeft: {
                xs: "none",
                md: "2px solid #E4C18F",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: {
                  xs: 2,
                  sm: 4,
                },
                mt: {
                  xs: 1,
                  sm: 2,
                },
                mr: {
                  xs: 0,
                  sm: 2,
                  md: 3,
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: {
                    xs: "20px",
                    sm: "24px",
                    md: "30px",
                  },
                  color: "primary.main",
                  fontFamily: "Onest",
                  lineHeight: 1.2,
                }}
              >
                {pageTitle}
              </Typography>
            </Box>

            <InfiniteScroll
              dataLength={products?.length || 0}
              next={fetchMoreProducts}
              hasMore={hasMore}
              scrollThreshold={0.8}
              loader={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 3,
                    width: "100%",
                  }}
                >
                  <CircularProgress size={28} sx={{ color: "primary.main" }} />
                </Box>
              }
              endMessage={
                products.length > 0 ? (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography
                      sx={{
                        fontFamily: "Be Vietnam Pro",
                        fontSize: "13px",
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    >
                      You've seen all products
                    </Typography>
                  </Box>
                ) : null
              }
              style={{ overflow: "visible" }}
            >
              <ProductCardsList products={products} loading={loading} />
            </InfiniteScroll>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default MobileAllGiftCardPage;
